import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import { AnalyticsService } from "@/services/analytics";
import { Sparkles, Crown, Circle, Loader2, Video } from "lucide-react";
import { cn } from "@/lib/utils";
import { AssetSelectZone } from "@/components/features/image-to-video";
import { PiecanNotice } from "@/components/features/piecan-notice";
import {
  Dropdown,
  GenerateButton,
  LockBadge,
  UpgradePlansModal,
  AuthModal,
  PromptInput,
  Skeleton,
  DropdownSkeleton,
  DurationSkeleton,
  MobileBottomBar,
  Toast,
  type DropdownOption,
  type UpgradePreset,
} from "@/components/ui";
import { AssetPickerModal } from "./asset-picker-modal";
import { DurationSelector } from "./duration-selector";
import { useI2VImage } from "./use-i2v-image";
import { useAutoPrompt } from "./use-auto-prompt";
import {
  type ModelType,
  CREDIT_COSTS,
  getModelId,
  LOCALSTORAGE_KEY,
} from "./config";
import { SCENE_PRESETS, DEFAULT_SCENE_ID } from "./scene-presets";
import {
  useUserStore,
  type PlanType,
  hasAccessToPlanLevel,
} from "@/stores/user-store";
import { hasAccessToPlan } from "@/config/navigation";
import { useActiveGenerations } from "@/hooks";

export function ImageToVideoForm() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isSignedIn, user: clerkUser } = useUser();
  const { plan: userPlan, credits, isLoading: isUserLoading } = useUserStore();
  const { hasActiveGeneration } = useActiveGenerations();
  const hasStarter = isSignedIn && hasAccessToPlan(userPlan, "starter");
  const hasCreator = isSignedIn && hasAccessToPlan(userPlan, "creator");

  // Check if user has Pro or Ultimate (unlimited concurrent generations)
  const hasUnlimitedConcurrent = hasAccessToPlanLevel(userPlan, "pro");

  // Image source state (asset selection only; uploads are handled
  // on the separate img2vid.pixelmaker.ai surface).
  const {
    imagePreview,
    imageS3Url,
    uploadId,
    inputGenerationId,
    folder,
    isImageLoading,
    error,
    handleRemoveImage,
    handleAssetSelect,
  } = useI2VImage();

  // Auto-prompt
  const autoPrompt = useAutoPrompt();

  // Form state
  const [prompt, setPrompt] = useState("");
  const [model, setModel] = useState<ModelType>("basic");
  const [duration, setDuration] = useState("5");
  const [sceneId, setSceneId] = useState<string>(DEFAULT_SCENE_ID);
  const [showAssetPicker, setShowAssetPicker] = useState(false);

  // Modal state
  const [upgradeModal, setUpgradeModal] = useState<{
    open: boolean;
    preset: UpgradePreset;
    creditsNeeded?: number;
    feature?: string;
    allowedPlans?: PlanType[];
  }>({ open: false, preset: "upgrade" });
  const [authModal, setAuthModal] = useState<{
    open: boolean;
    variant: "generate" | "feature";
    title?: string;
    description?: string;
  }>({ open: false, variant: "generate" });

  // Spinner state for non-logged-in generate flow
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const generatePreviewTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track whether we've already auto-triggered the auth modal for external redirects
  const hasAutoTriggeredAuth = useRef(false);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (generatePreviewTimer.current) clearTimeout(generatePreviewTimer.current);
    };
  }, []);

  // Track if user arrived from an external source (cloak site)
  const [isFromExternalSource, setIsFromExternalSource] = useState(false);

  // Handle URL search params (e.g., from cloak site redirect)
  const hasProcessedSearchParams = useRef(false);
  useEffect(() => {
    if (hasProcessedSearchParams.current) return;

    const urlPrompt = searchParams.get("prompt");
    const urlModel = searchParams.get("model");
    const urlUtmSource = searchParams.get("utm_source");
    const urlReferenceImage = searchParams.get("reference_image");

    // Detect external source (cloak site)
    if (urlPrompt || urlModel || urlUtmSource || urlReferenceImage) {
      setIsFromExternalSource(true);
    }

    if (!urlPrompt && !urlModel) return;

    hasProcessedSearchParams.current = true;

    // Apply prompt from URL
    if (urlPrompt) {
      setPrompt(urlPrompt);
    }

    // Apply model from URL (map cloak model names to frontend model names)
    if (urlModel) {
      const modelMap: Record<string, ModelType> = {
        basic: "basic",
        standard: "standard",
        premium: "premium",
      };
      const mappedModel = modelMap[urlModel.toLowerCase()];
      if (mappedModel) {
        setModel(mappedModel);
        // Set appropriate default duration for the model. All current ws_*
        // and wan_2_5 models offer 5s as a baseline; the duration selector
        // will widen to 10s when the user picks it on Standard/Premium.
        setDuration("5");
      }
    }

    // Clear the params from URL to prevent re-processing
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("prompt");
    newParams.delete("model");
    newParams.delete("reference_image"); // Also clean up (handled by useI2VImage)
    // Keep utm_source in the URL so it remains available for analytics/tracking
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Set defaults based on plan
  useEffect(() => {
    // Only set defaults if we haven't processed search params
    if (hasProcessedSearchParams.current) return;
    if (!isUserLoading && isSignedIn && hasStarter) {
      setModel("standard");
      setDuration("5");
    }
  }, [isUserLoading, isSignedIn, hasStarter]);

  // LocalStorage persistence for unauthenticated users
  useEffect(() => {
    if (!isSignedIn && (prompt.trim() || imagePreview)) {
      try {
        localStorage.setItem(
          LOCALSTORAGE_KEY,
          JSON.stringify({
            prompt,
            imageS3Url, // Store S3 URL, not blob URL
            uploadId,
            model,
            duration,
            timestamp: Date.now(),
          }),
        );
      } catch {
        /* ignore */
      }
    }
  }, [prompt, imagePreview, imageS3Url, uploadId, model, duration, isSignedIn]);

  useEffect(() => {
    if (!isSignedIn) return;
    const saved = localStorage.getItem(LOCALSTORAGE_KEY);
    if (!saved) return;
    try {
      const data = JSON.parse(saved);
      if (Date.now() - data.timestamp < 30 * 60 * 1000) {
        if (data.prompt) setPrompt(data.prompt);
        if (data.model === "standard" && hasStarter) setModel("standard");
        if (data.model === "premium" && hasCreator) setModel("premium");
        if (data.duration) setDuration(data.duration);
        
        // Restore image from temp upload S3 URL
        if (data.imageS3Url) {
          // Temp upload URL is still valid - use it directly
          handleAssetSelect(data.imageS3Url);
        }
        
        localStorage.removeItem(LOCALSTORAGE_KEY);
      } else {
        localStorage.removeItem(LOCALSTORAGE_KEY);
      }
    } catch {
      /* ignore */
    }
  }, [isSignedIn, hasStarter, hasCreator]);

  // Auto-trigger auth modal for non-signed-in users arriving from external redirect
  // with both prompt and image pre-filled (so they don't have to click "Generate Video")
  useEffect(() => {
    if (
      hasAutoTriggeredAuth.current ||
      !isFromExternalSource ||
      isSignedIn ||
      !prompt.trim() ||
      !imageS3Url ||
      isImageLoading
    ) {
      return;
    }

    hasAutoTriggeredAuth.current = true;

    // Same flow as handleGenerate for non-signed-in users: spinner → auth modal
    setIsGeneratingPreview(true);
    generatePreviewTimer.current = setTimeout(() => {
      setIsGeneratingPreview(false);
      setAuthModal({
        open: true,
        variant: "generate",
        title: "Sign in to view generation",
        description:
          "Create a free account to view your generation and start creating.",
      });
    }, 2500);
  }, [isFromExternalSource, isSignedIn, prompt, imageS3Url, isImageLoading]);

  const handleAutoPrompt = async () => {
    if (!imageS3Url) return;
    // Track Auto Prompt click
    AnalyticsService.trackAutoPromptClicked(clerkUser?.id, imageS3Url, "image_to_video");
    const result = await autoPrompt.enhance(imageS3Url);
    if (result) setPrompt(result);
  };

  const handleGenerate = useCallback(() => {
    if (!prompt.trim() || (!uploadId && !imageS3Url)) return;

    if (!isSignedIn) {
      // Show spinner for 2.5s, then open auth modal
      setIsGeneratingPreview(true);
      generatePreviewTimer.current = setTimeout(() => {
        setIsGeneratingPreview(false);
        setAuthModal({
          open: true,
          variant: "generate",
          title: "Sign in to view generation",
          description: "Create a free account to view your generation and start creating.",
        });
      }, 2500);
      return;
    }

    const modelId = getModelId(model, duration);
    const cost = CREDIT_COSTS[modelId] ?? 50;

    if (credits < cost) {
      AnalyticsService.trackOutOfCredits({
        credits_remaining: credits,
        credits_required: cost,
        model_attempted: modelId,
        generation_type: 'video',
        is_paid_user: userPlan !== 'free',
        user_plan: userPlan,
      });
      setUpgradeModal({
        open: true,
        preset: "no-credits",
        creditsNeeded: cost,
      });
      return;
    }

    // Check concurrent generation limit for non-Pro users
    if (hasActiveGeneration && !hasUnlimitedConcurrent) {
      setUpgradeModal({ open: true, preset: "concurrent-limit" });
      return;
    }

    localStorage.removeItem(LOCALSTORAGE_KEY);
    const scene = SCENE_PRESETS.find((s) => s.id === sceneId);
    const loraPreference =
      scene && scene.loraPreference !== "auto" ? scene.loraPreference : undefined;
    navigate("/generation/new", {
      state: {
        modelId,
        prompt: prompt.trim(),
        type: "video",
        generateParams: {
          model_id: modelId,
          prompt: prompt.trim(),
          upload_id: uploadId || undefined,
          input_generation_id: inputGenerationId || undefined,
          // Only use input_url as fallback if no upload_id or input_generation_id
          input_url:
            !uploadId && !inputGenerationId && imageS3Url
              ? imageS3Url
              : undefined,
          lora_preference: loraPreference,
          folder: folder || undefined,
        },
      },
    });
  }, [prompt, uploadId, imageS3Url, isSignedIn, model, duration, sceneId, folder, credits, userPlan, hasActiveGeneration, hasUnlimitedConcurrent, inputGenerationId, navigate, imageS3Url]);

  const handleLockedClick = (feature: string, plans: PlanType[]) => {
    if (!isSignedIn) {
      setAuthModal({ open: true, variant: "feature" });
    } else {
      setUpgradeModal({
        open: true,
        preset: "upgrade",
        feature,
        allowedPlans: plans,
      });
    }
  };

  const modelOptions: DropdownOption[] = useMemo(
    () => [
      { value: "basic", label: "Basic", icon: <Circle className="size-4" /> },
      {
        value: "standard",
        label: "Standard",
        icon: <Sparkles className="size-4" />,
        locked: !hasStarter,
        badge: !hasStarter ? <LockBadge variant="gold" /> : undefined,
      },
      {
        value: "premium",
        label: "Premium",
        description: "With audio",
        icon: <Crown className="size-4" />,
        locked: !hasCreator,
        badge: !hasCreator ? <LockBadge variant="violet" /> : undefined,
      },
    ],
    [hasStarter, hasCreator],
  );

  const isValid = (uploadId || imageS3Url) && prompt.trim();
  const modelId = getModelId(model, duration);
  const creditCost = CREDIT_COSTS[modelId] ?? 50;

  const generateButton = (
    <GenerateButton
      disabled={!isValid || isUserLoading || isGeneratingPreview}
      className="w-full h-full"
      onClick={handleGenerate}
    >
      {isGeneratingPreview ? (
        <>
          <Loader2 className="size-5 animate-spin" />
          Generating...
        </>
      ) : isUserLoading ? (
        <>
          <Video className="size-5" />
          Generate Video <Skeleton className="h-4 w-16 inline-block ml-1" />
        </>
      ) : (
        <>
          <Video className="size-5" />
          Generate Video{isSignedIn && <> ({creditCost} credits)</>}
        </>
      )}
    </GenerateButton>
  );

  return (
    <>
      <div className="space-y-4 pb-24 md:pb-0">
        {error && (
          <div className="flex items-center gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/20">
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {/* Partner upsell — pixelmaker.ai only lets users pick from their
            existing library here; the actual upload flow lives on PieCan now.
            Compact variant so it doesn't push the composer down. */}
        <PiecanNotice variant="compact" source="i2v_form" />

        {isImageLoading ? (
          <div className="flex flex-col items-center justify-center min-h-[280px] rounded-xl border-2 border-dashed border-white/25 bg-gradient-to-b from-white/[0.05] to-transparent">
            <Loader2 className="size-8 text-white/50 animate-spin" />
            <p className="mt-3 text-sm text-[#666]">Loading image...</p>
          </div>
        ) : (
          <AssetSelectZone
            onSelectFromAssets={() => setShowAssetPicker(true)}
            onRemove={handleRemoveImage}
            previewUrl={imagePreview}
          />
        )}

        <AssetPickerModal
          isOpen={showAssetPicker}
          onClose={() => setShowAssetPicker(false)}
          onSelect={(url, id, source, folder) => {
            handleAssetSelect(url, id, source, folder);
            setShowAssetPicker(false);
          }}
        />

        <PromptInput
          value={prompt}
          onChange={setPrompt}
          placeholder="Describe the motion..."
          rows={2}
          onAutoPrompt={handleAutoPrompt}
          isAutoPromptLoading={autoPrompt.isEnhancing}
          autoPromptDisabled={!imageS3Url}
        />

        {/* Scene picker — pins a motion LoRA so the backend doesn't have
            to keyword-detect the act from the prompt. "Auto" keeps the
            keyword-based selector in charge. Hidden when arriving from
            an external source while signed-out (matches model/duration
            hide rule above). */}
        {!(isFromExternalSource && !isSignedIn) && (
          <div className="relative rounded-xl p-[1px] bg-gradient-to-br from-white/[0.12] via-white/[0.04] to-white/[0.08] w-full md:w-[260px]">
            <Dropdown
              label="Scene"
              value={sceneId}
              onChange={(val) => setSceneId(val)}
              options={SCENE_PRESETS.map((s) => ({
                value: s.id,
                label: s.label,
              }))}
            />
          </div>
        )}

        <div className="flex flex-col md:flex-row gap-3">
          {/* Hide model and duration selectors for non-logged-in users from external sources */}
          {!(isFromExternalSource && !isSignedIn) && (
            <div className="flex gap-3 w-full md:w-auto">
              <div
                className={cn(
                  "relative rounded-xl p-[1px] w-1/2 md:w-[160px] shrink-0",
                  !isUserLoading && model === "premium"
                    ? "animate-border-rotate"
                    : "bg-gradient-to-br from-white/[0.12] via-white/[0.04] to-white/[0.08]",
                )}
              >
                {isUserLoading ? (
                  <DropdownSkeleton />
                ) : (
                  <Dropdown
                    label="Model"
                    value={model}
                    onChange={(val) => setModel(val as ModelType)}
                    glow={model === "premium"}
                    onLockedClick={(val) =>
                      handleLockedClick(
                        `${val === "standard" ? "Standard" : "Premium"} Model`,
                        val === "standard"
                          ? ["starter", "creator"]
                          : ["creator", "pro"],
                      )
                    }
                    options={modelOptions}
                  />
                )}
              </div>
              <div className="relative rounded-xl p-[1px] bg-gradient-to-br from-white/[0.12] via-white/[0.04] to-white/[0.08] w-1/2 md:w-auto">
                {isUserLoading ? (
                  <DurationSkeleton />
                ) : (
                  <DurationSelector
                    model={model}
                    value={duration}
                    onChange={setDuration}
                    hasCreatorAccess={hasCreator}
                    onLockedClick={() =>
                      handleLockedClick("Extended Duration", ["creator", "pro"])
                    }
                  />
                )}
              </div>
            </div>
          )}
          <div className="hidden md:block flex-1 self-stretch">
            {generateButton}
          </div>
        </div>
      </div>

      <MobileBottomBar>{generateButton}</MobileBottomBar>

      <UpgradePlansModal
        isOpen={upgradeModal.open}
        onClose={() => setUpgradeModal({ open: false, preset: "upgrade" })}
        preset={upgradeModal.preset}
        presetContext={{
          creditsNeeded: upgradeModal.creditsNeeded,
          currentCredits: credits,
          feature: upgradeModal.feature,
        }}
        allowedPlans={upgradeModal.allowedPlans}
      />
      <AuthModal
        isOpen={authModal.open}
        onClose={() => setAuthModal({ open: false, variant: "generate" })}
        variant={authModal.variant}
        title={authModal.title}
        description={authModal.description}
      />
      <Toast
        isOpen={autoPrompt.error}
        onClose={autoPrompt.clearError}
        type="error"
        message="Failed to generate prompt. Please try again."
        duration={3000}
      />
    </>
  );
}
